import { Upload } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Modal, ModalFooter } from "@/components/ui";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import { validateReceiptFileOrToast } from "@/lib/fileValidation";
import { FileDropzone } from "@/components/common/FileDropzone";

interface ReceiptOcrFailureModalProps {
  message: string;
  manualAmountRequired: boolean;
  remainingValidationAttempts?: number;
  maxValidationAttempts?: number;
  isProcessing: boolean;
  onFileRetry: (file: File) => void;
  onManualAmount: () => void;
}

export function ReceiptOcrFailureModal({
  message,
  manualAmountRequired,
  remainingValidationAttempts,
  maxValidationAttempts,
  isProcessing,
  onFileRetry,
  onManualAmount,
}: ReceiptOcrFailureModalProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("components");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showAttempts =
    remainingValidationAttempts != null &&
    maxValidationAttempts != null &&
    !manualAmountRequired;

  const handleFile = (file: File) => {
    if (
      !validateReceiptFileOrToast(file, {
        tooLarge: (max) => t("receiptUploadTooLarge", { max }),
        unsupported: t("receiptUploadUnsupported"),
      })
    ) {
      return;
    }
    onFileRetry(file);
  };

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen
      onClose={() => {}}
      title={t("receiptOcrFailureTitle")}
      size="sm"
      role="alertdialog"
      dismissOnBackdrop={false}
      showCloseButton={false}
      footer={
        <ModalFooter>
          <Button
            variant="primary"
            isLoading={isProcessing}
            leftIcon={<Upload className="h-4 w-4" />}
            onClick={() => fileInputRef.current?.click()}
          >
            {t("receiptOcrFailureRetry")}
          </Button>
          {manualAmountRequired ? (
            <Button
              variant="outline"
              disabled={isProcessing}
              onClick={onManualAmount}
            >
              {t("receiptOcrFailureManualAmount")}
            </Button>
          ) : null}
        </ModalFooter>
      }
    >
      <Alert variant="warning" className="mb-4">
        <p className="text-pretty">{message}</p>
        {showAttempts ? (
          <p className="mt-2 font-medium">
            {t("receiptOcrFailureAttemptsRemaining", {
              remaining: remainingValidationAttempts,
              max: maxValidationAttempts,
            })}
          </p>
        ) : null}
        {manualAmountRequired ? (
          <p className="mt-2">{t("receiptOcrFailureManualHint")}</p>
        ) : null}
      </Alert>

      <FileDropzone
        id="receipt-ocr-failure-retry"
        accept={FILE_ACCEPT.imageAndPdf}
        disabled={isProcessing}
        isLoading={isProcessing}
        inputRef={fileInputRef}
        icon={Upload}
        title={t("receiptOcrFailureRetry")}
        className="py-6"
        onFilesSelected={(files) => {
          const file = files[0];
          if (file) {
            handleFile(file);
          }
        }}
      />
    </Modal>
  );
}
